/**
 * MyVotesHistory Component
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 5: Create MyVotesHistory component
 *
 * Displays user's voting record with proposal titles, vote choices, dates,
 * and vote-pass NFT IDs.
 *
 * @see AC-4.2.8.4 - All user's past votes displayed with choices
 *
 * @module components/governance/MyVotesHistory
 */

import { Link } from 'react-router-dom';
import { FileText, Calendar, Award } from 'lucide-react';
import type { VoteHistoryItem, VoteChoice } from '../../services/governanceService';

// =============================================================================
// Types
// =============================================================================

export interface MyVotesHistoryProps {
  /** Array of vote history items */
  votes: VoteHistoryItem[];
}

// =============================================================================
// Constants
// =============================================================================

const VOTE_CHOICE_COLORS: Record<VoteChoice, string> = {
  Yes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  No: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Abstain: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  NeedsRefinement: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const VOTE_CHOICE_LABELS: Record<VoteChoice, string> = {
  Yes: 'Yes',
  No: 'No',
  Abstain: 'Abstain',
  NeedsRefinement: 'Needs Refinement',
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format date for display using user's locale
 */
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// Component
// =============================================================================

/**
 * User's voting history list
 */
export function MyVotesHistory({ votes }: MyVotesHistoryProps) {
  // BL-1057: defensive default — never index `.length`/`.map` on a non-array.
  // Even if a caller (or a misbehaving API mapping) hands us undefined/null,
  // the ErrorBoundary crash ("Cannot read properties of undefined") is
  // structurally impossible.
  const items = Array.isArray(votes) ? votes : [];

  // Empty state
  if (items.length === 0) {
    return (
      <div
        className="text-center py-12"
        data-testid="my-votes-empty"
      >
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">
          You haven't voted on any proposals yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-votes-history">
      {items.map((vote) => (
        <div
          key={vote.proposal_id}
          className="bg-card rounded-lg border border-border p-4"
          data-testid="vote-history-item"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Proposal link */}
              <Link
                to={`/proposals/${vote.proposal_id}`}
                className="font-medium text-foreground hover:text-primary transition-colors"
                data-testid={`vote-proposal-link-${vote.proposal_id}`}
              >
                {vote.proposal_title}
              </Link>

              {/* Meta info */}
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span
                  className="flex items-center gap-1"
                  data-testid={`vote-date-${vote.proposal_id}`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(vote.voted_at)}
                </span>
                <span
                  className="flex items-center gap-1"
                  data-testid={`vote-nft-${vote.proposal_id}`}
                >
                  <Award className="w-3.5 h-3.5" />
                  NFT #{vote.vote_pass_nft_id}
                </span>
              </div>
            </div>

            {/* Vote choice badge */}
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full ${VOTE_CHOICE_COLORS[vote.vote_choice]}`}
              data-testid={`vote-choice-${vote.proposal_id}`}
            >
              {VOTE_CHOICE_LABELS[vote.vote_choice]}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MyVotesHistory;
